import { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { brandButton, ghostButton, textBody } from '../styles/tokens'
import { AuthContext } from '../hooks/useAuth'
import {
  getConsent,
  optInAnalytics,
  optOutAnalytics,
} from '../lib/analytics'
import * as api from '../lib/api'

// CookieBanner asks for first-party-analytics consent before PostHog is
// opted in. PECR / ICO 2023 guidance treats product analytics as
// non-essential, so we default to opted-out and surface this banner on
// first visit. The choice persists in localStorage (`cookie_consent`,
// values `accepted` | `rejected`) and the banner stays hidden after.
//
// Mounted unconditionally from App.tsx so it appears on every route
// including the auth pages (a returning logged-out user gets the same
// chance to consent as a logged-in one).
export default function CookieBanner() {
  const { t } = useTranslation('common')
  // Start hidden so SSR / first paint doesn't flash a banner before we
  // know the consent state. `useEffect` reads localStorage post-mount;
  // a tiny render-flicker beats a banner that briefly covers the login
  // form for users who already chose.
  const [show, setShow] = useState(false)
  // useContext directly so the banner is render-safe inside a Shell
  // mounted without an AuthProvider (Shell.test.tsx exercises this).
  // user is null for anonymous visitors; the persist() helper short-
  // circuits in that case and keeps the choice browser-local.
  const auth = useContext(AuthContext)
  const user = auth?.user ?? null

  useEffect(() => {
    if (getConsent() === null) setShow(true)
  }, [])

  if (!show) return null

  async function persist(value: 'accepted' | 'rejected') {
    // Always write localStorage first (handled by optIn/Out), then mirror
    // to the server for logged-in users so their choice follows the
    // account to other browsers. Anonymous visitors stay browser-local
    // (no account row to attach the choice to yet — on first login the
    // useAuth bootstrap sync pushes their local choice up).
    if (!user) return
    try {
      await api.updatePreferences({ analytics_consent: value })
    } catch {
      // Server write failed; local state is set, banner won't reappear
      // on this browser. The next Settings → Privacy interaction will
      // retry. Silent — a network-error toast at the bottom of a
      // consent banner would be more confusing than helpful.
    }
  }

  function handleAccept() {
    optInAnalytics()
    setShow(false)
    void persist('accepted')
  }

  function handleReject() {
    optOutAnalytics()
    setShow(false)
    void persist('rejected')
  }

  return (
    <div
      role="region"
      aria-label={t('cookie_banner.region_label')}
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-4 sm:pb-6"
    >
      <div
        className="pointer-events-auto flex w-full max-w-3xl flex-col gap-3 rounded-2xl border border-white/80 bg-white/85 p-4 shadow-v2-card backdrop-blur-[10px] dark:border-[rgba(230,237,243,0.15)] dark:bg-v2-bg-dark/90 dark:shadow-v2-card-dark sm:flex-row sm:items-center sm:justify-between sm:gap-4"
      >
        <p className={`text-xs ${textBody} sm:text-sm`}>
          {t('cookie_banner.message')}{' '}
          <Link
            to="/privacy"
            className="underline underline-offset-2 hover:text-v2-purple dark:hover:text-v2-purple-dark"
          >
            {t('cookie_banner.learn_more_link')}
          </Link>
          .
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={handleReject}
            className={`${ghostButton} px-4 py-2 text-xs font-medium`}
          >
            {t('cookie_banner.reject_button')}
          </button>
          <button
            type="button"
            onClick={handleAccept}
            className={`${brandButton} px-4 py-2 text-xs`}
          >
            {t('cookie_banner.accept_button')}
          </button>
        </div>
      </div>
    </div>
  )
}
